import { requireSupabase } from './supabase';
import type {
  CourseItem,
  CourseModule,
  CourseNote,
  CoursePlayerPreferences,
  CourseProgress,
  Entitlement,
  Stage,
  Volume,
} from '../types/hub';

export async function fetchVolumes(): Promise<Volume[]> {
  const { data, error } = await requireSupabase()
    .from('volumes')
    .select('*')
    .eq('published', true)
    .order('sort_order');
  if (error) throw error;
  return (data ?? []) as Volume[];
}

export async function fetchEntitlements(): Promise<Entitlement[]> {
  const { data, error } = await requireSupabase()
    .from('entitlements')
    .select('*')
    .eq('active', true);
  if (error) throw error;
  return (data ?? []) as Entitlement[];
}

export async function fetchVolumeBySlug(slug: string): Promise<Volume | null> {
  const { data, error } = await requireSupabase()
    .from('volumes')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return data as Volume | null;
}

export async function fetchVolumeStructure(volumeId: string): Promise<{ stages: Stage[]; modules: CourseModule[] }> {
  const client = requireSupabase();
  const [stagesResult, modulesResult] = await Promise.all([
    client.from('stages').select('*').eq('volume_id', volumeId).eq('published', true).order('sort_order'),
    client.from('modules').select('*').eq('volume_id', volumeId).eq('published', true).order('sort_order'),
  ]);
  if (stagesResult.error) throw stagesResult.error;
  if (modulesResult.error) throw modulesResult.error;
  return {
    stages: (stagesResult.data ?? []) as Stage[],
    modules: (modulesResult.data ?? []) as CourseModule[],
  };
}

export async function isVolumeEntitled(volumeId: string): Promise<boolean> {
  const { data, error } = await requireSupabase().rpc('is_entitled', {
    target_volume_id: volumeId,
    target_resource_key: 'volume_content',
  });
  if (error) throw error;
  return Boolean(data);
}

export async function fetchCourseItems(volumeId: string, includeDrafts = false): Promise<CourseItem[]> {
  let query = requireSupabase()
    .from('course_items')
    .select('*, course_media(*)')
    .eq('volume_id', volumeId)
    .order('sort_order');
  if (!includeDrafts) query = query.eq('published', true);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as CourseItem[];
}

export async function fetchCourseProgress(userId: string, itemIds: string[]): Promise<CourseProgress[]> {
  if (!itemIds.length) return [];
  const { data, error } = await requireSupabase()
    .from('course_progress')
    .select('*')
    .eq('user_id', userId)
    .in('course_item_id', itemIds);
  if (error) throw error;
  return (data ?? []) as CourseProgress[];
}

export async function fetchCoursePlayerPreferences(userId: string): Promise<CoursePlayerPreferences> {
  const client = requireSupabase();
  const { data, error } = await client
    .from('course_player_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  if (data) return data as CoursePlayerPreferences;

  const defaults: CoursePlayerPreferences = {
    user_id: userId,
    show_video: true,
    show_audio: true,
    show_transcript: true,
    show_module_nav: true,
    playback_rate: 1,
    last_course_item_id: null,
  };
  const { error: insertError } = await client.from('course_player_preferences').insert(defaults);
  if (insertError) throw insertError;
  return defaults;
}

export async function saveCoursePlayerPreferences(
  userId: string,
  patch: Partial<Omit<CoursePlayerPreferences, 'user_id'>>,
): Promise<void> {
  const { error } = await requireSupabase().from('course_player_preferences').upsert({
    user_id: userId,
    ...patch,
  });
  if (error) throw error;
}

export async function touchCourseProgress(
  userId: string,
  courseItemId: string,
  patch: Partial<Pick<CourseProgress, 'completed_at' | 'last_position_seconds' | 'last_format'>> = {},
): Promise<void> {
  const { error } = await requireSupabase().from('course_progress').upsert({
    user_id: userId,
    course_item_id: courseItemId,
    last_viewed_at: new Date().toISOString(),
    ...patch,
  });
  if (error) throw error;
}

export async function fetchSavedCourseItemIds(userId: string, itemIds: string[]): Promise<string[]> {
  if (!itemIds.length) return [];
  const { data, error } = await requireSupabase()
    .from('saved_course_items')
    .select('course_item_id')
    .eq('user_id', userId)
    .in('course_item_id', itemIds);
  if (error) throw error;
  return (data ?? []).map((row) => row.course_item_id as string);
}

export async function setCourseItemSaved(userId: string, courseItemId: string, saved: boolean): Promise<void> {
  const client = requireSupabase();
  if (saved) {
    const { error } = await client.from('saved_course_items').upsert({ user_id: userId, course_item_id: courseItemId });
    if (error) throw error;
  } else {
    const { error } = await client.from('saved_course_items').delete().eq('user_id', userId).eq('course_item_id', courseItemId);
    if (error) throw error;
  }
}

export async function fetchCourseNotes(userId: string, itemIds: string[]): Promise<CourseNote[]> {
  if (!itemIds.length) return [];
  const { data, error } = await requireSupabase()
    .from('course_notes')
    .select('*')
    .eq('user_id', userId)
    .in('course_item_id', itemIds);
  if (error) throw error;
  return (data ?? []) as CourseNote[];
}

export async function saveCourseNote(userId: string, courseItemId: string, body: string): Promise<CourseNote> {
  const { data, error } = await requireSupabase()
    .from('course_notes')
    .upsert({ user_id: userId, course_item_id: courseItemId, body }, { onConflict: 'user_id,course_item_id' })
    .select('*')
    .single();
  if (error) throw error;
  return data as CourseNote;
}

export function entitlementActive(entitlement: Entitlement): boolean {
  if (!entitlement.active) return false;
  const now = Date.now();
  const starts = new Date(entitlement.starts_at).getTime();
  const ends = entitlement.ends_at ? new Date(entitlement.ends_at).getTime() : null;
  return starts <= now && (entitlement.is_lifetime || ends === null || ends > now);
}

export function ownsVolume(entitlements: Entitlement[], volumeId: string): boolean {
  return entitlements.some(
    (item) => item.volume_id === volumeId && item.resource_key === 'volume_content' && entitlementActive(item),
  );
}

export async function fetchWorkbookTemplates(volumeId: string) {
  const { data, error } = await requireSupabase().from('workbook_templates').select('*').eq('volume_id', volumeId).eq('published', true).order('sort_order');
  if (error) throw error;
  return data ?? [];
}
export async function fetchWorkbookResponses(userId: string, templateIds: string[]) {
  if (!templateIds.length) return [];
  const { data, error } = await requireSupabase().from('workbook_responses').select('*').eq('user_id', userId).in('template_id', templateIds);
  if (error) throw error;
  return data ?? [];
}
export async function saveWorkbookResponse(userId: string, templateId: string, responses: Record<string,string>, status: string) {
  const completed_at = status === 'complete' ? new Date().toISOString() : null;
  const { data, error } = await requireSupabase().from('workbook_responses').upsert({ user_id:userId, template_id:templateId, responses, status, completed_at }, { onConflict:'user_id,template_id' }).select('*').single();
  if (error) throw error;
  return data;
}
export async function fetchAnchors(userId: string, volumeId: string) {
  const { data, error } = await requireSupabase().from('anchors').select('*').eq('user_id', userId).eq('volume_id', volumeId).eq('is_current', true).order('created_at');
  if (error) throw error;
  return data ?? [];
}
export async function saveAnchor(input: { user_id:string; volume_id:string; module_id:string; template_id:string; anchor_key:string; title:string; body:string }) {
  const client = requireSupabase();
  const { data: existing, error: existingError } = await client.from('anchors').select('*').eq('user_id', input.user_id).eq('volume_id', input.volume_id).eq('anchor_key', input.anchor_key).eq('is_current', true).maybeSingle();
  if (existingError) throw existingError;
  if (existing) {
    const { data, error } = await client.from('anchors').update({ title:input.title, body:input.body }).eq('id', existing.id).select('*').single();
    if (error) throw error; return data;
  }
  const { data, error } = await client.from('anchors').insert({ ...input, version:1, is_current:true }).select('*').single();
  if (error) throw error; return data;
}
export async function fetchBlueprint(userId: string, volumeId: string) {
  const { data, error } = await requireSupabase().from('blueprints').select('*').eq('user_id',userId).eq('volume_id',volumeId).maybeSingle();
  if (error) throw error; return data;
}
export async function saveBlueprint(userId:string, volumeId:string, patch:{title:string;season_statement:string;notes:string}) {
  const { data,error } = await requireSupabase().from('blueprints').upsert({user_id:userId,volume_id:volumeId,...patch},{onConflict:'user_id,volume_id'}).select('*').single();
  if(error) throw error; return data;
}
export async function createBlueprintVersion(blueprintId:string,userId:string,version:number,snapshot:Record<string,unknown>) {
  const client=requireSupabase();
  const {error}=await client.from('blueprint_versions').insert({blueprint_id:blueprintId,user_id:userId,version,snapshot});
  if(error) throw error;
  const {error:updateError}=await client.from('blueprints').update({current_version:version}).eq('id',blueprintId);
  if(updateError) throw updateError;
}
export async function fetchBlueprintVersions(userId:string, blueprintId:string) {
  const {data,error}=await requireSupabase().from('blueprint_versions').select('*').eq('user_id',userId).eq('blueprint_id',blueprintId).order('version',{ascending:false});
  if(error) throw error; return data??[];
}
